import type { Message } from '@centraoneplus/shared-types';

/**
 * Reading a call inside the conversation thread.
 *
 * Voice turns are ordinary ticket messages — the ticket-service writes one per
 * turn — so they already appear in the thread alongside anything an agent
 * typed later. What they carry that a typed message does not is the language
 * the turn was spoken in and, when that was not English, an English rendering.
 *
 * These helpers are the seam between that metadata and the thread's existing
 * voice branch, kept pure so the behaviour can be tested without rendering.
 */

const LANGUAGE_NAMES: Record<string, string> = {
  'en-IN': 'English',
  'hi-IN': 'Hindi',
  'bn-IN': 'Bengali',
  'gu-IN': 'Gujarati',
  'kn-IN': 'Kannada',
  'ml-IN': 'Malayalam',
  'mr-IN': 'Marathi',
  'od-IN': 'Odia',
  'pa-IN': 'Punjabi',
  'ta-IN': 'Tamil',
  'te-IN': 'Telugu',
};

export interface VoiceTurn {
  /** What was actually said, in the language it was said in. */
  original: string;
  /** An English rendering, when the turn was not already English. */
  english?: string;
  language?: string;
  languageLabel?: string;
}

function isEnglish(code: string): boolean {
  const base = code.toLowerCase().split(/[-_]/)[0];
  return base === 'en';
}

export function languageName(code?: string): string | undefined {
  if (!code) return undefined;
  // The call transcript stores a regional code ("mr-IN"); the chat stores the
  // plain one ("mr"). Both must read as "Marathi".
  const base = code.toLowerCase().split(/[-_]/)[0];
  return (
    LANGUAGE_NAMES[code] ??
    LANGUAGE_NAMES[`${base}-IN`] ??
    code
  );
}

/**
 * The voice details of one message, or null if it is not a call turn.
 *
 * Keyed on the `voiceTranscript` flag the consumer sets, so an agent's own
 * typed replies on the same ticket are never treated as something the caller
 * said — and so email and chat threads are untouched.
 */
export function readVoiceTurn(message: Message | undefined | null): VoiceTurn | null {
  const meta = (message?.metadata ?? {}) as Record<string, any>;

  const english = typeof meta.contentEnglish === 'string' ? meta.contentEnglish.trim() : '';
  const language = typeof meta.language === 'string' ? meta.language : undefined;

  // Voice turns are always call turns. A chat turn qualifies once it carries a
  // translation, or a language that is not English - the chat now stores both,
  // the same way the call transcript does. An agent's own typed reply carries
  // neither, so it is still never treated as something the customer said, and
  // an all-English chat gains no language label it does not need.
  const isTranslatedTurn =
    Boolean(meta.voiceTranscript) || Boolean(english) || Boolean(language && !isEnglish(language));
  if (!isTranslatedTurn) return null;
  return {
    original: message?.content ?? '',
    // A blank translation counts as none: otherwise the toggle offers an
    // "English" view that renders nothing.
    english: english || undefined,
    language,
    languageLabel: languageName(language),
  };
}

/**
 * Whether an English/as-spoken toggle is worth showing on this thread.
 *
 * False for an all-English call, so a control that would do nothing never
 * appears.
 */
export function threadHasTranslations(messages: Message[] | undefined | null): boolean {
  if (!Array.isArray(messages)) return false;
  return messages.some((m) => Boolean(readVoiceTurn(m)?.english));
}

/**
 * Which text to show for a turn, given the current toggle.
 *
 * Always falls back to the original — a turn whose translation failed must
 * still be readable, rather than rendering blank.
 */
export function displayedText(turn: VoiceTurn, showEnglish: boolean): string {
  return showEnglish && turn.english ? turn.english : turn.original;
}

/** Whether the original should be shown underneath the displayed text. */
export function showsOriginalBeneath(turn: VoiceTurn, showEnglish: boolean): boolean {
  return Boolean(showEnglish && turn.english);
}
